import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import { List, X } from "lucide-react";

interface TocItem {
  id: string;
  labelKey: string;
  fallback: string;
}

const items: TocItem[] = [
  { id: "challenges", labelKey: "toc.challenges", fallback: "Retos" },
  { id: "platform-showcase", labelKey: "toc.platform", fallback: "Plataforma" },
  { id: "xplora", labelKey: "toc.xplora", fallback: "Xplora" },
  { id: "solutions", labelKey: "toc.solutions", fallback: "Soluciones" },
  { id: "how-it-works", labelKey: "toc.howItWorks", fallback: "Cómo funciona" },
  { id: "capabilities", labelKey: "toc.capabilities", fallback: "Capacidades" },
  { id: "critical-env", labelKey: "toc.criticalEnv", fallback: "Entornos críticos" },
  { id: "enterprise", labelKey: "toc.enterprise", fallback: "Enterprise" },
  { id: "use-cases", labelKey: "toc.useCases", fallback: "Casos de uso" },
  { id: "benefits", labelKey: "toc.benefits", fallback: "Beneficios" },
  { id: "why-qualita", labelKey: "toc.whyQualita", fallback: "Por qué Qualita" },
  { id: "regional", labelKey: "toc.regional", fallback: "Presencia regional" },
  { id: "brochure", labelKey: "toc.brochure", fallback: "Brochure" },
  { id: "faq", labelKey: "toc.faq", fallback: "FAQ" },
  { id: "cta", labelKey: "toc.cta", fallback: "Contacto" },
];

const TableOfContents = () => {
  const { t } = useTranslation();
  const [active, setActive] = useState<string>("");
  const [open, setOpen] = useState(false);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > 600);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const visibleEntries = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);
        if (visibleEntries[0]) setActive(visibleEntries[0].target.id);
      },
      { rootMargin: "-30% 0px -60% 0px", threshold: [0, 0.25, 0.5, 1] }
    );
    items.forEach((it) => {
      const el = document.getElementById(it.id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  const handleClick = (id: string) => {
    const el = document.getElementById(id);
    if (el) {
      const top = el.getBoundingClientRect().top + window.scrollY - 80;
      window.scrollTo({ top, behavior: "smooth" });
      setOpen(false);
    }
  };

  if (!visible) return null;

  return (
    <div className="fixed bottom-24 left-4 sm:left-6 z-40">
      <div className="relative flex flex-col items-start">
        {/* Toggle button */}
        <motion.button
          onClick={() => setOpen((o) => !o)}
          whileTap={{ scale: 0.95 }}
          className="w-12 h-12 rounded-full bg-card/90 backdrop-blur-md border border-border/60 shadow-lg flex items-center justify-center text-foreground hover:bg-card transition-colors"
          aria-label={t("toc.label", "Índice de contenido")}
          aria-expanded={open}
        >
          <motion.div
            animate={{ rotate: open ? 90 : 0 }}
            transition={{ duration: 0.2 }}
          >
            {open ? <X className="w-5 h-5" /> : <List className="w-5 h-5" />}
          </motion.div>
        </motion.button>

        {/* Expanded menu */}
        <AnimatePresence>
          {open && (
            <motion.div
              initial={{ opacity: 0, y: 10, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 10, scale: 0.95 }}
              transition={{ duration: 0.18, ease: "easeOut" }}
              className="absolute bottom-14 left-0 w-60 max-h-[70vh] overflow-y-auto rounded-2xl bg-card/95 backdrop-blur-md border border-border/60 shadow-2xl p-2"
              role="menu"
            >
              <div className="px-3 py-2 text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">
                {t("toc.label", "Índice de contenido")}
              </div>
              <ul className="flex flex-col gap-0.5">
                {items.map((it, idx) => {
                  const isActive = active === it.id;
                  return (
                    <motion.li
                      key={it.id}
                      initial={{ opacity: 0, x: -8 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: idx * 0.015 }}
                    >
                      <button
                        onClick={() => handleClick(it.id)}
                        role="menuitem"
                        className={`w-full text-left text-sm px-3 py-2 rounded-lg transition-colors flex items-center gap-2 ${
                          isActive
                            ? "bg-primary/10 text-primary font-medium"
                            : "text-foreground/80 hover:bg-muted"
                        }`}
                      >
                        <span
                          className={`block h-1.5 w-1.5 rounded-full transition-colors ${
                            isActive ? "bg-primary" : "bg-muted-foreground/40"
                          }`}
                        />
                        {t(it.labelKey, it.fallback)}
                      </button>
                    </motion.li>
                  );
                })}
              </ul>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default TableOfContents;
